'use client';
import { useResource } from '@/lib/use-resource';

type HealthState = 'ok' | 'warn' | 'fail' | 'unknown';
interface SubIndicator { label: string; state: HealthState; value: string }
interface HealthBlock {
  state: HealthState;
  reason: string;
  checkedAt: string;
  detail?: string;
  sub?: SubIndicator;
}
interface HealthResponse {
  sapi: HealthBlock;
  terminal: HealthBlock;
  fmp: HealthBlock;
}
interface PipelinePull { id: string; category?: string }
interface PipelineRun { date: string; pulls: Record<string, { status: string; dataPoints: number | null }> }
interface PipelineResp { today: string; pulls: PipelinePull[]; runs: PipelineRun[] }

// Bloomberg pulls that REQUIRE Terminal USER auth.
// Post-2026-05-19 entitlement audit: only options-chain genuinely needs Terminal.
// Everything else (BEST_*, analyst consensus, fundamentals, earnings) works
// APP-only via the SAPI license — see data/bbg-entitlement-matrix.json.
const TERMINAL_PULL_IDS = new Set(['options-chain']);

interface SourceCount { points: number; pullsDone: number; pullsTotal: number }
function aggregate(p: PipelineResp | undefined | null): { sapi: SourceCount; terminal: SourceCount; fmp: SourceCount } {
  const zero = { points: 0, pullsDone: 0, pullsTotal: 0 };
  if (!p) return { sapi: zero, terminal: zero, fmp: zero };
  const todayRun = p.runs.find((r) => r.date === p.today);
  const bbgIds = new Set(p.pulls.filter((x) => x.category === 'bbg').map((x) => x.id));
  // SAPI-only = bbg pulls that work APP-only (no Terminal required).
  const sapiOnlyIds = new Set([...bbgIds].filter((id) => !TERMINAL_PULL_IDS.has(id)));
  // Terminal = bbg pulls that require Terminal USER auth.
  const terminalIds = new Set([...bbgIds].filter((id) => TERMINAL_PULL_IDS.has(id)));
  const fmpIds  = new Set(p.pulls.filter((x) => x.category === 'fmp').map((x) => x.id));
  function tally(ids: Set<string>): SourceCount {
    if (!todayRun) return { points: 0, pullsDone: 0, pullsTotal: ids.size };
    let points = 0, pullsDone = 0;
    for (const id of ids) {
      const r = todayRun.pulls[id];
      if (!r) continue;
      points += r.dataPoints ?? 0;
      if (r.status === 'ok') pullsDone += 1;
    }
    return { points, pullsDone, pullsTotal: ids.size };
  }
  return { sapi: tally(sapiOnlyIds), terminal: tally(terminalIds), fmp: tally(fmpIds) };
}

function fmtCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return n.toLocaleString();
}

const TONE: Record<HealthState, { dot: string; ring: string; label: string; bg: string; text: string; bar: string }> = {
  ok:      { dot: 'bg-emerald-400 shadow-[0_0_6px_rgba(74,222,128,0.7)]',           ring: 'border-emerald-400/30', label: 'text-emerald-300', bg: 'bg-emerald-400/5',  text: 'OK',   bar: 'bg-emerald-400' },
  warn:    { dot: 'bg-amber-300 shadow-[0_0_6px_rgba(252,211,77,0.7)] animate-pulse', ring: 'border-amber-400/30',  label: 'text-amber-300',   bg: 'bg-amber-400/5',    text: 'WARN', bar: 'bg-amber-300' },
  fail:    { dot: 'bg-red-400 shadow-[0_0_6px_rgba(248,113,113,0.85)] animate-pulse', ring: 'border-red-400/40',    label: 'text-red-300',     bg: 'bg-red-400/5',      text: 'DOWN', bar: 'bg-red-400' },
  unknown: { dot: 'bg-zinc-500/70 ring-1 ring-zinc-400/60',                           ring: 'border-zinc-500/30',   label: 'text-zinc-300',    bg: 'bg-zinc-500/5',     text: '?',    bar: 'bg-zinc-500' },
};

function fmtAge(iso: string): string {
  const t = new Date(iso).getTime();
  if (isNaN(t)) return '—';
  const ageS = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (ageS < 60) return `${ageS}s ago`;
  if (ageS < 3600) return `${Math.round(ageS / 60)}m ago`;
  if (ageS < 86400) return `${Math.round(ageS / 3600)}h ago`;
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function Row({ name, what, h, count, showCount }: { name: string; what: string; h: HealthBlock; count: SourceCount; showCount: boolean }) {
  const tone = TONE[h.state];
  const pct = count.pullsTotal > 0 ? Math.round((count.pullsDone / count.pullsTotal) * 100) : 0;
  return (
    <div className={`flex items-center gap-3 border ${tone.ring} ${tone.bg} rounded px-3 py-2`}>
      <span className={`inline-block w-2.5 h-2.5 rounded-full ${tone.dot} flex-shrink-0`} />
      <div className="flex items-baseline gap-2 min-w-0 flex-1">
        <span className="text-[11px] font-semibold tracking-wider uppercase text-white whitespace-nowrap">{name}</span>
        <span className="text-[9px] text-[#64748b] whitespace-nowrap">{what}</span>
        <span className={`text-[10px] font-mono ${tone.label} truncate`}>{h.reason}</span>
        {h.sub && (
          <span className="text-[9px] text-[#94a3b8] truncate hidden md:inline">· {h.sub.label}: <span className={TONE[h.sub.state].label}>{h.sub.value}</span></span>
        )}
        {h.detail && <span className="text-[9px] text-[#64748b] truncate hidden lg:inline">· {h.detail}</span>}
      </div>
      {showCount ? (
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="flex flex-col items-end leading-none gap-0.5">
            <span className="text-[9px] text-[#64748b] tabular-nums">data pulled today</span>
            <span className="text-[11px] font-mono tabular-nums text-white">{fmtCount(count.points)} pts</span>
          </div>
          <div className="flex flex-col items-end leading-none gap-0.5">
            <span className="text-[9px] text-[#64748b] tabular-nums">pulls done</span>
            <span className="text-[11px] font-mono tabular-nums text-white">{count.pullsDone}/{count.pullsTotal}</span>
          </div>
          <div className="w-24 h-1.5 rounded-full bg-slate-700/40 overflow-hidden">
            <div className={`h-full ${tone.bar} transition-[width] duration-500 ease-out`} style={{ width: `${pct}%` }} />
          </div>
          <span className="text-[10px] font-mono tabular-nums text-amber-300 w-9 text-right">{pct}%</span>
        </div>
      ) : (
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-[9px] text-[#64748b] italic">auth gate · not a data source</span>
        </div>
      )}
      <span className={`text-[9px] font-semibold tracking-wider ${tone.label} whitespace-nowrap`}>{tone.text}</span>
      <span className="text-[9px] text-[#64748b] tabular-nums whitespace-nowrap">{fmtAge(h.checkedAt)}</span>
    </div>
  );
}

export function HealthBanners() {
  const { data: health } = useResource<HealthResponse>('/api/health-check', 20_000);
  const { data: pipeline } = useResource<PipelineResp>('/api/pipeline-status', 30_000);
  const counts = aggregate(pipeline);
  if (!health) {
    return (
      <div className="grid grid-cols-1 gap-1.5">
        {['SAPI', 'TERMINAL', 'FMP'].map((n) => (
          <div key={n} className="flex items-center gap-3 border border-zinc-500/30 bg-zinc-500/5 rounded px-3 py-2">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-zinc-500/70 flex-shrink-0" />
            <span className="text-[11px] font-semibold tracking-wider uppercase text-white">{n}</span>
            <span className="text-[10px] text-[#64748b]">checking…</span>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-1.5">
      <Row name="SAPI"     what="Bloomberg APP-auth pulls (no Terminal needed)"  h={health.sapi}     count={counts.sapi}     showCount={true} />
      <Row name="Terminal" what="Bloomberg Terminal USER auth"        h={health.terminal} count={counts.terminal} showCount={true} />
      <Row name="FMP"      what="Financial Modeling Prep quote API"   h={health.fmp}      count={counts.fmp}      showCount={true} />
    </div>
  );
}
